/**
 * SBN Scroll Animation Engine
 * - Fade-up reveal  : data-sbn-animate
 * - Stagger group   : data-sbn-stagger (children animate in sequence)
 * - Number counter  : data-sbn-counter="1234" (optionally data-sbn-suffix="+")
 * - Split text      : data-sbn-split (chars/words slide up one by one)
 */
(function () {
  'use strict';

  /* ─── Config ─────────────────────────────────────────── */
  var CFG = {
    rootMargin : '0px 0px -8% 0px',   // trigger when 8% from bottom
    threshold  : 0.01,
    baseDuration : 0.72,              // seconds
    baseDelay    : 0.0,
    staggerStep  : 0.12,              // seconds between stagger children
    counterDuration : 1600,           // ms for number count-up
    easingCSS : 'cubic-bezier(0.22, 1, 0.36, 1)',
  };

  /* ─── Utility ─────────────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  /* ─── 1. Fade-Up Reveal ───────────────────────────────── */
  function initFadeUp() {
    var els = qsa('[data-sbn-animate]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay    = parseFloat(el.dataset.sbnDelay    || 0);
        var duration = parseFloat(el.dataset.sbnDuration || CFG.baseDuration);
        el.style.transitionDelay    = delay + 's';
        el.style.transitionDuration = duration + 's';
        el.style.transitionTimingFunction = CFG.easingCSS;
        el.classList.add('sbn-revealed');
        io.unobserve(el);
      });
    }, { rootMargin: CFG.rootMargin, threshold: CFG.threshold });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ─── 2. Stagger Group ────────────────────────────────── */
  function initStagger() {
    var groups = qsa('[data-sbn-stagger]');
    if (!groups.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var group     = entry.target;
        var step      = parseFloat(group.dataset.sbnStaggerDelay || CFG.staggerStep);
        var baseDelay = parseFloat(group.dataset.sbnDelay || 0);
        var children  = Array.from(group.children);

        children.forEach(function (child, i) {
          var delay = baseDelay + i * step;
          child.style.transitionDelay    = delay + 's';
          child.style.transitionDuration = CFG.baseDuration + 's';
          child.style.transitionTimingFunction = CFG.easingCSS;
          // mark children as animatable if not already
          if (!child.hasAttribute('data-sbn-animate')) {
            child.setAttribute('data-sbn-animate', '');
          }
          setTimeout(function () {
            child.classList.add('sbn-revealed');
          }, delay * 1000);
        });
        io.unobserve(group);
      });
    }, { rootMargin: CFG.rootMargin, threshold: CFG.threshold });

    groups.forEach(function (g) { io.observe(g); });
  }

  /* ─── 3. Number Counter ───────────────────────────────── */
  function parseNumber(str) {
    // extract numeric part, keep suffix/prefix
    var match = String(str).match(/^([^0-9\-]*)(-?[\d,\.]+)([^0-9]*)$/);
    if (!match) return null;
    return {
      prefix : match[1],
      value  : parseFloat(match[2].replace(/,/g, '')),
      suffix : match[3],
      original: str
    };
  }

  function formatNum(n, decimals) {
    if (decimals > 0) return n.toFixed(decimals);
    return Math.round(n).toLocaleString();
  }

  function animateCounter(el) {
    var raw      = el.dataset.sbnCounter;
    var parsed   = parseNumber(raw || el.textContent.trim());
    if (!parsed || isNaN(parsed.value)) return;

    var target   = parsed.value;
    var decimals = (String(target).split('.')[1] || '').length;
    var suffix   = (el.dataset.sbnSuffix || parsed.suffix || '');
    var prefix   = (el.dataset.sbnPrefix || parsed.prefix || '');
    var duration = parseInt(el.dataset.sbnCounterDuration || CFG.counterDuration, 10);
    var start    = performance.now();

    function tick(now) {
      var elapsed = now - start;
      var progress = Math.min(elapsed / duration, 1);
      var eased    = easeOutExpo(progress);
      el.textContent = prefix + formatNum(target * eased, decimals) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function initCounters() {
    // auto-detect: elements with data-sbn-counter OR .sc-val with numeric text
    var explicit = qsa('[data-sbn-counter]');
    var autoDetect = qsa('.sc-val, .fc-stat-num, .sbn-stat-num').filter(function (el) {
      return !el.hasAttribute('data-sbn-counter') && /^[\s\d,\.\+\-\%x×]+$/.test(el.textContent.trim());
    });

    var all = explicit.concat(autoDetect);
    if (!all.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        // store original text for re-trigger support
        if (!el.dataset.sbnCounter) {
          el.dataset.sbnCounter = el.textContent.trim();
        }
        var delay = parseFloat(el.dataset.sbnDelay || 0) * 1000;
        setTimeout(function () { animateCounter(el); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: CFG.rootMargin, threshold: CFG.threshold });

    all.forEach(function (el) { io.observe(el); });
  }

  /* ─── 4. Split Text (word-level slide-up) ─────────────── */
  function splitWords(el) {
    var text  = el.textContent.trim();
    var words = text.split(/\s+/);
    el.innerHTML = words.map(function (w) {
      return '<span class="sbn-word-wrap"><span class="sbn-word">' + w + '</span></span>';
    }).join(' ');
    return el.querySelectorAll('.sbn-word');
  }

  function initSplitText() {
    var els = qsa('[data-sbn-split]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el        = entry.target;
        var baseDelay = parseFloat(el.dataset.sbnDelay    || 0);
        var step      = parseFloat(el.dataset.sbnSplitStep || 0.055);
        var words     = splitWords(el);

        words.forEach(function (word, i) {
          word.style.transitionDelay    = (baseDelay + i * step) + 's';
          word.style.transitionDuration = '0.6s';
          word.style.transitionTimingFunction = CFG.easingCSS;
          setTimeout(function () {
            word.classList.add('sbn-word--visible');
          }, (baseDelay + i * step) * 1000);
        });
        io.unobserve(el);
      });
    }, { rootMargin: CFG.rootMargin, threshold: CFG.threshold });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ─── 5. Auto-tag SBN sections ───────────────────────── */
  function autoTag() {
    // Headings in sbn-* sections: fade-up
    qsa('.sbn-section h1, .sbn-section h2, .sbn-section h3').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-animate') && !el.hasAttribute('data-sbn-split')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = (0.05 + i * 0.04).toFixed(2);
      }
    });

    // statement section — split headline
    qsa('.stmt-line1, .stmt-line2').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-split')) {
        el.setAttribute('data-sbn-split', '');
        el.dataset.sbnDelay    = (0.1 + i * 0.18).toFixed(2);
        el.dataset.sbnSplitStep = '0.045';
      }
    });
    qsa('.stmt-cite').forEach(function (el) {
      if (!el.hasAttribute('data-sbn-animate')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = '0.55';
      }
    });

    // feature-cards body text
    qsa('.fc-tag, .fc-title, .fc-body').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-animate')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = (0.08 + (i % 3) * 0.1).toFixed(2);
      }
    });

    // three-modes header
    qsa('.tm-header .tm-eyebrow, .tm-header .tm-headline').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-animate')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = (0.05 + i * 0.14).toFixed(2);
      }
    });

    // scene sections
    qsa('.sbn-scene h2, .sbn-scene p, .scene-headline, .scene-body, .scene-tag').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-animate')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = (0.08 + i * 0.12).toFixed(2);
      }
    });

    // specs-center values → counter animation
    qsa('.sc-val').forEach(function (el) {
      if (!el.hasAttribute('data-sbn-counter') && /[\d]/.test(el.textContent)) {
        el.dataset.sbnCounter = el.textContent.trim();
      }
    });

    // sc-label fade
    qsa('.sc-label').forEach(function (el, i) {
      if (!el.hasAttribute('data-sbn-animate')) {
        el.setAttribute('data-sbn-animate', '');
        el.dataset.sbnDelay = (0.1 + (i % 8) * 0.06).toFixed(2);
      }
    });

    // hero headline (if not managed by hero section itself)
    qsa('.hfm-headline:not([data-sbn-split])').forEach(function (el) {
      el.setAttribute('data-sbn-animate', '');
      el.dataset.sbnDelay = '0.2';
    });
    qsa('.hfm-subtext').forEach(function (el) {
      el.setAttribute('data-sbn-animate', '');
      el.dataset.sbnDelay = '0.42';
    });
    qsa('.hfm-eyebrow').forEach(function (el) {
      el.setAttribute('data-sbn-animate', '');
      el.dataset.sbnDelay = '0.08';
    });
  }

  /* ─── Init ────────────────────────────────────────────── */
  function init() {
    autoTag();
    initSplitText();   // must run before fadeUp (split replaces DOM)
    initFadeUp();
    initStagger();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor hot-reload
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 80);
  });

})();
